import { DialogVoiceMessage, TypeDialogMessage } from "../../types/DialogChat";
import TypeDefaultResponse from "../../types/TypeDefaultResponse";

export const DIALOG_CHAT_VOICE_MAX_SIZE_BYTES = 25 * 1024 * 1024;
export const DIALOG_CHAT_VOICE_MAX_DURATION_SECONDS = 5 * 60;
export const DIALOG_CHAT_VOICE_MIME_TYPES = [
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
];

export type DialogChatVoiceDraft = {
  id: string;
  file: File;
  url: string;
  durationSeconds?: number;
};

export type DialogChatVoiceValidationResult =
  | { valid: true }
  | { valid: false; reason: "unsupported-type" | "too-large" | "too-long" };

export type DialogChatVoiceApi = {
  generateDialogVoiceUploadUrl: (
    token: string,
    payload: {
      dialogChatId: string;
      originalName: string;
      mimeType: string;
      size: number;
      durationSeconds?: number;
    },
  ) => Promise<
    TypeDefaultResponse & { url?: string; voice?: DialogVoiceMessage }
  >;
  sendMessageToDialogChat: (payload: {
    userId?: string;
    dialogChatId: string;
    messageType: "voice";
    date: string;
    voice: DialogVoiceMessage;
  }) => Promise<
    TypeDefaultResponse & {
      newMessage?: TypeDialogMessage | null;
      responseMessage?: TypeDialogMessage | null;
    }
  >;
  getDialogMessageById: (
    token: string,
    messageId: string,
  ) => Promise<TypeDefaultResponse & { responseMessage?: TypeDialogMessage }>;
};

export const getSupportedDialogChatVoiceMimeType = () => {
  if (typeof MediaRecorder === "undefined") {
    return "audio/webm";
  }

  return (
    DIALOG_CHAT_VOICE_MIME_TYPES.find((mimeType) =>
      MediaRecorder.isTypeSupported(mimeType),
    ) || "audio/webm"
  );
};

export const validateDialogChatVoiceFile = (
  file: File,
  durationSeconds?: number,
): DialogChatVoiceValidationResult => {
  if (!DIALOG_CHAT_VOICE_MIME_TYPES.includes(file.type)) {
    return { valid: false, reason: "unsupported-type" };
  }

  if (file.size > DIALOG_CHAT_VOICE_MAX_SIZE_BYTES) {
    return { valid: false, reason: "too-large" };
  }

  if (
    durationSeconds &&
    durationSeconds > DIALOG_CHAT_VOICE_MAX_DURATION_SECONDS
  ) {
    return { valid: false, reason: "too-long" };
  }

  return { valid: true };
};

export const stopDialogChatVoiceStream = (stream?: MediaStream | null) => {
  stream?.getTracks().forEach((track) => track.stop());
};

export const uploadAndSendDialogChatVoiceMessage = async ({
  api,
  token,
  userId,
  chatId,
  draft,
}: {
  api: Pick<
    DialogChatVoiceApi,
    "generateDialogVoiceUploadUrl" | "sendMessageToDialogChat"
  >;
  token: string;
  userId?: string;
  chatId: string;
  draft: DialogChatVoiceDraft;
}): Promise<TypeDialogMessage> => {
  const upload = await api.generateDialogVoiceUploadUrl(token, {
    dialogChatId: chatId,
    originalName: draft.file.name,
    mimeType: draft.file.type,
    size: draft.file.size,
    durationSeconds: draft.durationSeconds,
  });

  if (!upload.status || !upload.url || !upload.voice) {
    throw new Error(upload.message || "Voice upload was rejected");
  }

  const uploadMimeType = upload.voice.mimeType || draft.file.type;
  const uploadResponse = await fetch(upload.url, {
    method: "PUT",
    headers: uploadMimeType ? { "Content-Type": uploadMimeType } : undefined,
    body: draft.file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`Voice upload failed (${uploadResponse.status})`);
  }

  const response = await api.sendMessageToDialogChat({
    userId,
    dialogChatId: chatId,
    messageType: "voice",
    date: new Date().toISOString(),
    voice: upload.voice,
  });

  const sentMessage = response.newMessage || response.responseMessage;

  if (!response.status || !sentMessage) {
    throw new Error(response.message || "Voice message was not sent");
  }

  return sentMessage;
};

export const refreshDialogChatVoicePlaybackUrl = async ({
  api,
  token,
  messageId,
}: {
  api: Pick<DialogChatVoiceApi, "getDialogMessageById">;
  token: string;
  messageId: string;
}): Promise<DialogVoiceMessage | undefined> => {
  const response = await api.getDialogMessageById(token, messageId);

  if (!response.status || !response.responseMessage?.voice) {
    return undefined;
  }

  return response.responseMessage.voice;
};
